/**
 * PAPPO — API Service (fetch wrapper com JWT auto-inject)
 * Wrapper tipado para comunicação com o backend Flask.
 * Injeta automaticamente o JWT e trata erros de forma consistente.
 */

import type { ApiResponse } from '../types/api';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

/**
 * Retorna os headers padrão com JWT se disponível
 */
function getHeaders(contentType = 'application/json'): HeadersInit {
  const headers: Record<string, string> = {};
  if (contentType) headers['Content-Type'] = contentType;

  const token = localStorage.getItem('pappo_access_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;

  return headers;
}

/**
 * Trata a resposta do fetch, parseando JSON e gerindo erros
 */
async function handleResponse<T>(
  response: Response,
  noAuthRedirect = false
): Promise<ApiResponse<T>> {
  // Um 401 num endpoint de AUTENTICAÇÃO (login, etc.) é erro de credenciais —
  // NÃO é sessão expirada. Sem `noAuthRedirect`, o interceptor recarregava a
  // tela antes de a mensagem de "senha incorreta" aparecer.
  if (response.status === 401 && !noAuthRedirect) {
    // Token expirado — tentar refresh
    const refreshed = await tryRefreshToken();
    if (!refreshed) {
      localStorage.removeItem('pappo_access_token');
      localStorage.removeItem('pappo_refresh_token');
      window.location.href = '/login';
      return { success: false, error: 'Sessão expirada. Faz login novamente.' };
    }
  }

  try {
    const data = await response.json();
    if (!response.ok) {
      return {
        success: false,
        error: data.error || data.message || `Erro ${response.status}`,
      };
    }
    return { success: true, data };
  } catch {
    return {
      success: false,
      error: `Erro inesperado (${response.status})`,
    };
  }
}

/**
 * Tenta renovar o access token usando o refresh token
 */
async function tryRefreshToken(): Promise<boolean> {
  const refreshToken = localStorage.getItem('pappo_refresh_token');
  if (!refreshToken) return false;

  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) return false;

    const data = await res.json();
    localStorage.setItem('pappo_access_token', data.access_token);
    return true;
  } catch {
    return false;
  }
}

/** GET request tipado */
export async function apiGet<T>(path: string): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'GET',
    headers: getHeaders(),
  });
  return handleResponse<T>(res);
}

/** POST request tipado. `opts.noAuthRedirect` desliga o interceptor de 401
 *  (usado nos endpoints de auth, onde 401 = credenciais, não sessão expirada). */
export async function apiPost<T>(
  path: string,
  body?: unknown,
  opts?: { noAuthRedirect?: boolean }
): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: getHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res, opts?.noAuthRedirect);
}

/** PUT request tipado */
export async function apiPut<T>(
  path: string,
  body?: unknown
): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<T>(res);
}

/** DELETE request tipado */
export async function apiDelete<T>(path: string): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return handleResponse<T>(res);
}

/**
 * Upload multipart (FormData). getHeaders('') omite o Content-Type para o
 * browser definir o boundary automaticamente, mantendo o Authorization.
 */
export async function apiUpload<T>(
  path: string,
  formData: FormData
): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: getHeaders(''),
    body: formData,
  });
  return handleResponse<T>(res);
}
